import { useState, useEffect, useCallback, useRef } from "react";
import Icon from "../icons/Icon";
import { useSearchParams } from "react-router-dom";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../firebase/config";
import { useAuth } from "../../hooks/useAuth";
import { useSubscription } from "../../hooks/useSubscription";
import { useToast } from "../../contexts/ToastContext";
import { SUBSCRIPTION_STATUS } from "../../config/subscription";
import { INCLUDED } from "../../config/plans";
import { peso, fmtDate } from "../../utils/billingFormat";
import ConfirmModal from "../modals/ConfirmModal";
import "../../styles/components/settings-pages.css";

// Dev-only: gate on REACT_APP_ENVIRONMENT (not NODE_ENV — react-scripts forces
// NODE_ENV=production for the uat build too; only REACT_APP_ENVIRONMENT tells uat
// from prod). So the simulator stays usable on sahod-uat, excluded from prod.
// (The devSetSubscription callable independently refuses on sahod-production.)
const SIMULATOR_ENABLED = process.env.REACT_APP_ENVIRONMENT !== "production";

// Statuses where a real Stripe subscription is live (Subscribe is hidden; the
// simulator is locked so it can't clobber real Stripe state).
const LIVE_STATUSES = ["active", "trialing", "past_due", "paused", "unpaid"];

const functions = getFunctions(undefined, "asia-southeast1");
const createCheckoutSessionFn = httpsCallable(functions, "createCheckoutSession");
const createCustomerPortalSessionFn = httpsCallable(functions, "createCustomerPortalSession");
// Cancel runs through Sahod (not the Portal) for the audit trail + Resume UX. Cancel
// schedules cancel_at_period_end; resume clears it. See cancelSubscription.js.
const cancelSubscriptionFn = httpsCallable(functions, "cancelSubscription");
const resumeSubscriptionFn = httpsCallable(functions, "resumeSubscription");
// Client can't write companies/{cid}.subscription (rules deny it) — the simulator
// routes through this Admin-SDK callable (dev/uat only; refuses on prod).
const devSetSubscriptionFn = httpsCallable(functions, "devSetSubscription");

const PHASE_BADGE = {
  trial: { label: "Trial", bg: "var(--ui-primary-tint)", fg: "var(--ui-primary-hover)" },
  active: { label: "Active", bg: "var(--ui-success-tint)", fg: "var(--ui-success-text)" },
  grace: { label: "Grace (payment due)", bg: "var(--ui-warning-tint)", fg: "var(--ui-warning-text)" },
  expired: { label: "Expired", bg: "var(--ui-danger-tint)", fg: "var(--ui-danger-text)" },
};

// Stripe's fixed cancellation_details.feedback enum (mirrored verbatim) behind
// friendly labels. Reason is optional — feedback is nullable in Stripe's API, so
// reason-friction never blocks a cancel. Free text goes in the separate `comment`.
// KEEP IN SYNC with STRIPE_CANCELLATION_FEEDBACK in
// functions/src/billing/cancelSubscription.js (separate build root — can't share a
// module; a `value` not in that Set is silently dropped server-side).
const CANCEL_REASONS = [
  { value: "too_expensive", label: "Too expensive" },
  { value: "missing_features", label: "Missing features I need" },
  { value: "switched_service", label: "Switching to another service" },
  { value: "too_complex", label: "Too hard to use" },
  { value: "unused", label: "Not using it enough" },
  { value: "low_quality", label: "Quality wasn't what I expected" },
  { value: "customer_service", label: "Customer service issue" },
  { value: "other", label: "Other" },
];

// Poll the company doc until `predicate(subscription)` holds or attempts run out
// (~6×2s). Shared by the post-checkout finalize and the cancel/resume handlers — the
// "mutate Stripe → await the webhook-synced mirror" pattern. `isCancelled()` lets the
// caller abort on unmount / effect teardown so we don't read or setState after the fact.
const pollCompanyDoc = async (cid, predicate, isCancelled) => {
  for (let i = 0; i < 6; i++) {
    if (isCancelled && isCancelled()) return;
    try {
      const snap = await getDoc(doc(db, "companies", cid));
      if (snap.exists() && predicate(snap.data()?.subscription || {})) return;
    } catch (e) {
      /* transient read error — retry */
    }
    await new Promise((r) => setTimeout(r, 2000));
  }
};

const SubscriptionsPage = () => {
  const { company, refreshCompany } = useAuth();
  const {
    status,
    phase,
    daysLeft,
    canManageBilling,
    canViewBilling,
    billingEstimate,
    runningCycleMax,
    stripeSubscriptionId,
    cancelAtPeriodEnd,
    currentPeriodEnd,
  } = useSubscription();
  const toast = useToast();
  const companyId = company?.id || null;

  const [busy, setBusy] = useState(false); // simulator writes
  const [action, setAction] = useState(null); // 'subscribe' | 'manage' | 'cancel' | 'resume'
  const [finalizing, setFinalizing] = useState(false); // post-checkout webhook lag window
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState(""); // Stripe feedback enum value (optional)
  const [cancelComment, setCancelComment] = useState(""); // free-text comment (optional)
  const [advStatus, setAdvStatus] = useState(SUBSCRIPTION_STATUS.TRIALING);
  const [advDays, setAdvDays] = useState(14);
  const [spendThreshold, setSpendThreshold] = useState(""); // string for the input
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const finalizeHandled = useRef(false); // one-shot guard (StrictMode-safe)
  const isMountedRef = useRef(true); // guards async cancel/resume setState after unmount

  const hasLiveSub = !!stripeSubscriptionId && LIVE_STATUSES.includes(status);

  // Track mount so the cancel/resume handlers (event-driven, no effect cleanup of
  // their own) can abort their poll + skip setState once the page unmounts.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ── Post-Checkout return (?checkout=success|cancel) ──────────────────────────
  // On success the customer.subscription.created webhook that writes
  // stripeSubscriptionId lags by seconds — during that window the Firestore mirror
  // still shows no sub, so without suppression the "Subscribe" button reappears and
  // a re-click opens a SECOND Checkout → double-bill (Step-9 review). So we set a
  // `finalizing` flag that hides Subscribe and poll the company doc until the sub
  // id lands (or a few attempts). The server also refuses a 2nd checkout by checking
  // Stripe directly (createCheckoutSession), closing the window fully.
  useEffect(() => {
    const checkout = searchParams.get("checkout");
    if (!checkout || !companyId || finalizeHandled.current) return undefined;
    finalizeHandled.current = true;

    // Clear the param — clone, never mutate the live searchParams object.
    const next = new URLSearchParams(searchParams);
    next.delete("checkout");
    setSearchParams(next, { replace: true });

    if (checkout === "cancel") {
      toast.info("Checkout canceled — no changes made.");
      return undefined;
    }

    toast.success("Checkout complete — finalizing your subscription…");
    setFinalizing(true);
    let cancelled = false;
    (async () => {
      await pollCompanyDoc(companyId, (sub) => !!sub.stripeSubscriptionId, () => cancelled);
      if (cancelled) return;
      // Always clear the flag, even if the refresh throws — otherwise Subscribe
      // stays suppressed until a manual reload (the bug the inline message masked).
      try {
        await refreshCompany();
      } finally {
        setFinalizing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, searchParams, setSearchParams, toast, refreshCompany]);

  // ── Spend-alert threshold (settings/billing) ─────────────────────────────────
  useEffect(() => {
    if (!company?.id || !canViewBilling) return;
    getDoc(doc(db, "companies", company.id, "settings", "billing"))
      .then((snap) => {
        const t = snap.exists() ? snap.data()?.spendAlertThreshold : null;
        setSpendThreshold(t != null ? String(t) : "");
      })
      .catch(() => {}); // permission/missing → leave blank
  }, [company?.id, canViewBilling]);

  // ── Stripe actions (callables) ───────────────────────────────────────────────
  const runRedirect = useCallback(
    async (which, fn) => {
      setAction(which);
      try {
        const { data } = await fn({ origin: window.location.origin });
        if (!data?.url) throw new Error("No redirect URL returned.");
        window.location.href = data.url; // → Stripe (Checkout / Portal)
        // Belt: if the navigation is blocked (extension) and the page doesn't
        // unload, re-enable the buttons rather than leaving them stuck disabled.
        setTimeout(() => setAction(null), 8000);
      } catch (e) {
        toast.error(e?.message || "Something went wrong. Please try again.");
        setAction(null);
      }
    },
    [toast],
  );

  const handleSubscribe = () => runRedirect("subscribe", createCheckoutSessionFn);
  const handleManage = () => runRedirect("manage", createCustomerPortalSessionFn);

  // ── Cancel / Resume (callable, then poll the webhook-synced flip) ─────────────
  const closeCancelModal = () => {
    setShowCancelConfirm(false);
    setCancelReason("");
    setCancelComment("");
  };

  // Cancel/resume don't redirect — they call the callable, then poll the company doc
  // until the customer.subscription.updated webhook syncs cancelAtPeriodEnd, then
  // refresh. The poll + refresh are best-effort (both ops are idempotent), so they run
  // OUTSIDE the callable's try/catch — only a callable failure surfaces an error toast
  // (a stale-doc refresh after a succeeded cancel must not read as a cancel failure).
  const pollCancelAtPeriodEnd = (expected) =>
    pollCompanyDoc(
      companyId,
      (sub) => Boolean(sub.cancelAtPeriodEnd) === expected,
      () => !isMountedRef.current,
    );

  const handleCancel = async () => {
    if (!companyId || action) return; // ignore re-entry (e.g. double-click)
    setAction("cancel");
    try {
      await cancelSubscriptionFn({
        reason: cancelReason || undefined,
        comment: cancelComment.trim() || undefined,
      });
    } catch (e) {
      toast.error(e?.message || "Could not cancel the subscription. Please try again.");
      setAction(null); // keep the modal open for retry
      return;
    }
    // Callable succeeded — everything below is best-effort UI sync.
    closeCancelModal();
    toast.success("Your subscription will end at the close of the current billing period.");
    try {
      await pollCancelAtPeriodEnd(true);
      await refreshCompany();
    } catch (e) {
      /* best-effort — the next load reflects the change */
    } finally {
      if (isMountedRef.current) setAction(null);
    }
  };

  const handleResume = async () => {
    if (!companyId || action) return; // ignore re-entry (e.g. double-click)
    setAction("resume");
    try {
      await resumeSubscriptionFn();
    } catch (e) {
      toast.error(e?.message || "Could not resume the subscription. Please try again.");
      setAction(null);
      return;
    }
    toast.success("Your subscription has been resumed.");
    try {
      await pollCancelAtPeriodEnd(false);
      await refreshCompany();
    } catch (e) {
      /* best-effort — the next load reflects the change */
    } finally {
      if (isMountedRef.current) setAction(null);
    }
  };

  const saveThreshold = async () => {
    if (!company?.id) return;
    const n = Number(spendThreshold);
    // Blank / non-positive → disable the alert (stored as null). Reset the per-cycle
    // notified marker so the new threshold is re-evaluated on the next nightly run.
    const value = spendThreshold.trim() === "" || !(n > 0) ? null : Math.round(n);
    setSavingThreshold(true);
    try {
      await setDoc(
        doc(db, "companies", company.id, "settings", "billing"),
        { spendAlertThreshold: value, spendAlertNotifiedCycleKey: null },
        { merge: true },
      );
      toast.success(value ? `Spend alert set at ${peso(value)}.` : "Spend alert disabled.");
    } catch (e) {
      toast.error(e?.message || "Could not save the spend alert.");
    } finally {
      setSavingThreshold(false);
    }
  };

  // ── Dev simulator (non-prod; locked when a real Stripe sub exists) ───────────
  // Routes through the devSetSubscription callable (Admin SDK) — clients can't write
  // the subscription field directly. payload: {status, days} | {clear: true}.
  const applySim = async (payload) => {
    setBusy(true);
    try {
      await devSetSubscriptionFn(payload);
      await refreshCompany();
    } catch (e) {
      console.error("Simulator failed:", e);
      toast.error(e?.message || "Simulator write failed.");
    } finally {
      setBusy(false);
    }
  };

  const badge = PHASE_BADGE[phase] || PHASE_BADGE.trial;

  return (
    <div>
      <h1 className="settings-page-title">Subscriptions</h1>
      <p className="settings-page-subtitle">Manage your billing and subscription</p>

      {/* Current status + actions */}
      <div className="settings-card" style={{ padding: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ background: badge.bg, color: badge.fg, borderRadius: 999, padding: "4px 12px", fontWeight: 600, fontSize: "0.8rem" }}>
            {badge.label}
          </span>
          <span style={{ color: "var(--ui-text-muted)", fontSize: "0.875rem" }}>
            {status ? `Stripe status: ${status}` : "No subscription on file (treated as trial)"}
            {daysLeft != null && ` · ${daysLeft} day${daysLeft === 1 ? "" : "s"} left`}
            {status === "active" && currentPeriodEnd && !cancelAtPeriodEnd && ` · renews ${fmtDate(currentPeriodEnd)}`}
          </span>
        </div>

        {/* Cancel-scheduled notice — only while the sub is still LIVE. Stripe keeps
            cancel_at_period_end:true on a canceled sub, so without the hasLiveSub gate
            this "full access until {date}" notice would wrongly persist on an Expired
            card. Tracks the Resume button (same gate). Guard on currentPeriodEnd too so
            the brief webhook-lag window (flag true, date not yet mirrored) reads cleanly. */}
        {hasLiveSub && cancelAtPeriodEnd && (
          <p
            style={{
              marginTop: 12,
              marginBottom: 0,
              fontSize: "0.875rem",
              color: "var(--ui-warning-text)",
              background: "var(--ui-warning-tint)",
              padding: "10px 14px",
              borderRadius: 8,
              lineHeight: 1.5,
            }}
          >
            {currentPeriodEnd
              ? `Your subscription is set to end on ${fmtDate(currentPeriodEnd)}. You'll keep full access until then — resume anytime before that date to undo.`
              : "Your subscription is set to end at the close of the current billing period. You'll keep full access until then — resume anytime before then to undo."}
          </p>
        )}

        {canManageBilling ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 20, alignItems: "center" }}>
            {/* No inline "finalizing" text — the success toast already says it, and
                the button flip (Subscribe → Manage Card) is the real signal.
                `finalizing` still suppresses Subscribe during the webhook-lag window. */}
            {!hasLiveSub && !finalizing && (
              <button className="btn btn--primary" disabled={!!action} onClick={handleSubscribe}>
                {/* "Resubscribe" only when a prior (now-lapsed) sub is on file; a
                    first-timer/trial with no stripeSubscriptionId sees "Subscribe".
                    Same action either way — opens Stripe Checkout. */}
                {action === "subscribe"
                  ? "Opening checkout…"
                  : stripeSubscriptionId ? "Resubscribe" : "Subscribe"}
              </button>
            )}
            {/* Card management only when there's a LIVE sub — when expired/canceled the
                portal can't reactivate, and Subscribe (Checkout) collects a fresh card. */}
            {hasLiveSub && (
              <button className="btn btn--secondary" disabled={!!action} onClick={handleManage}>
                {action === "manage" ? "Opening…" : "Manage Card"}
              </button>
            )}
            {/* Resume undoes a scheduled cancel. Prominent because it's the recovery path. */}
            {hasLiveSub && cancelAtPeriodEnd && (
              <button className="btn btn--primary" disabled={!!action} onClick={handleResume}>
                {action === "resume" ? "Resuming…" : "Resume subscription"}
              </button>
            )}
            {/* Quiet cancel link — only for a healthy sub (not past_due/paused, where the
                path is Manage Card / let-it-lapse) and only when not already cancelling. */}
            {hasLiveSub && (status === "active" || status === "trialing") && !cancelAtPeriodEnd && (
              <button
                className="btn btn--link"
                disabled={!!action}
                onClick={() => setShowCancelConfirm(true)}
              >
                Cancel subscription
              </button>
            )}
          </div>
        ) : (
          <p style={{ marginTop: 16, fontSize: "0.875rem", color: "var(--ui-text-muted)" }}>
            You don't have permission to manage billing. Contact a superuser.
          </p>
        )}
      </div>

      {/* Billing estimate (band breakdown) */}
      {canViewBilling && (
        <div className="settings-card" style={{ padding: 24, marginTop: 16 }}>
          <h2 style={{ fontSize: "1rem", fontWeight: 600, color: "var(--ui-text)", marginBottom: 4 }}>
            Estimated next invoice
          </h2>
          <p style={{ fontSize: "0.8rem", color: "var(--ui-text-muted)", marginBottom: 16 }}>
            Based on {runningCycleMax} employee{runningCycleMax === 1 ? "" : "s"} (the highest active count so far
            this cycle). The final bill rises if headcount grows before the cycle closes.
          </p>
          <div style={{ fontSize: "0.9rem", color: "var(--ui-text-secondary)", lineHeight: 1.8 }}>
            <div>{peso(billingEstimate.base)} base <span style={{ color: "var(--ui-text-faint)" }}>(covers 0–{INCLUDED})</span></div>
            {billingEstimate.breakdown.map((b) => (
              <div key={`${b.from}-${b.to ?? "inf"}`}>
                + {b.count} × {peso(b.rate)}{" "}
                <span style={{ color: "var(--ui-text-faint)" }}>(emp {b.from}{b.to == null ? "+" : `–${b.to}`})</span> = {peso(b.amount)}
              </div>
            ))}
            <div style={{ marginTop: 8, fontWeight: 700, fontSize: "1.05rem", color: "var(--ui-primary)" }}>
              {peso(billingEstimate.total)} / month projected
            </div>
          </div>
        </div>
      )}

      {/* Spend alert config */}
      {canViewBilling && (
        <div className="settings-card" style={{ padding: 24, marginTop: 16 }}>
          <h2 style={{ fontSize: "1rem", fontWeight: 600, color: "var(--ui-text)", marginBottom: 4 }}>Spend alert</h2>
          <p style={{ fontSize: "0.8rem", color: "var(--ui-text-muted)", marginBottom: 16, lineHeight: 1.5 }}>
            Email all superusers when the projected monthly invoice exceeds this amount. Informational only — it never
            blocks adding employees. Leave blank to disable.
          </p>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
            <div>
              <label style={{ display: "block", fontSize: "0.75rem", color: "var(--ui-text-muted)", marginBottom: 4 }}>
                Threshold (₱ / month)
              </label>
              <input
                type="number" min="0" placeholder="e.g. 20000" value={spendThreshold}
                onChange={(e) => setSpendThreshold(e.target.value)}
                disabled={!canManageBilling || savingThreshold}
                style={{ width: 160, padding: "8px 10px", borderRadius: 8, border: "1px solid var(--ui-border)" }}
              />
            </div>
            {canManageBilling && (
              <button className="btn btn--primary" disabled={savingThreshold} onClick={saveThreshold}>
                {savingThreshold ? "Saving…" : "Save"}
              </button>
            )}
          </div>
          {!canManageBilling && (
            <p style={{ fontSize: "0.75rem", color: "var(--ui-text-faint)", marginTop: 8 }}>Only a billing superuser can change this.</p>
          )}
        </div>
      )}

      {/* Dev simulator — non-prod only; LOCKED when a real Stripe sub exists */}
      {SIMULATOR_ENABLED && (
        <div className="settings-card" style={{ padding: 24, marginTop: 16 }}>
          <h2 style={{ fontSize: "1rem", fontWeight: 600, color: "var(--ui-text)", marginBottom: 4 }}>
            <Icon name="flask-conical" size={16} aria-hidden="true" /> Subscription Simulator <span style={{ color: "var(--ui-text-faint)", fontWeight: 400 }}>(dev / uat only)</span>
          </h2>
          {hasLiveSub ? (
            <p style={{ fontSize: "0.8rem", color: "var(--ui-warning-text)", background: "var(--ui-warning-tint)", borderRadius: 8, padding: "10px 12px", lineHeight: 1.5 }}>
              Locked — a real Stripe subscription is active (<code>{stripeSubscriptionId}</code>). The simulator is
              disabled so it can't clobber live Stripe state. Use <strong>Manage Card</strong> instead.
            </p>
          ) : (
            <p style={{ fontSize: "0.8rem", color: "var(--ui-text-muted)", marginBottom: 16, lineHeight: 1.5 }}>
              Writes the real company doc — on shared uat this affects everyone in the company until you hit{" "}
              <strong>Clear</strong>.
            </p>
          )}

          {!hasLiveSub && (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
                <button className="btn btn--secondary" disabled={busy} onClick={() => applySim({ status: SUBSCRIPTION_STATUS.TRIALING, days: 10 })}>Trialing (10d)</button>
                <button className="btn btn--secondary" disabled={busy} onClick={() => applySim({ status: SUBSCRIPTION_STATUS.ACTIVE, days: 30 })}>Active</button>
                <button className="btn btn--secondary" disabled={busy} onClick={() => applySim({ status: SUBSCRIPTION_STATUS.PAST_DUE, days: 3 })}>Grace (3d left)</button>
                <button className="btn btn--secondary" disabled={busy} onClick={() => applySim({ status: SUBSCRIPTION_STATUS.PAST_DUE, days: -1 })}>Grace EXPIRED</button>
                <button className="btn btn--secondary" disabled={busy} onClick={() => applySim({ status: SUBSCRIPTION_STATUS.CANCELED, days: 0 })}>Canceled</button>
                <button className="btn btn--danger" disabled={busy} onClick={() => applySim({ clear: true })}>Clear (→ default trial)</button>
              </div>

              <div style={{ borderTop: "1px solid var(--ui-border)", paddingTop: 16, display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <label style={{ display: "block", fontSize: "0.75rem", color: "var(--ui-text-muted)", marginBottom: 4 }}>Status</label>
                  <select value={advStatus} onChange={(e) => setAdvStatus(e.target.value)} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid var(--ui-border)" }}>
                    {Object.values(SUBSCRIPTION_STATUS).map((s) => (<option key={s} value={s}>{s}</option>))}
                  </select>
                </div>
                {(advStatus === SUBSCRIPTION_STATUS.TRIALING || advStatus === SUBSCRIPTION_STATUS.ACTIVE || advStatus === SUBSCRIPTION_STATUS.PAST_DUE || advStatus === SUBSCRIPTION_STATUS.PAUSED) && (
                  <div>
                    <label style={{ display: "block", fontSize: "0.75rem", color: "var(--ui-text-muted)", marginBottom: 4 }}>
                      {advStatus === SUBSCRIPTION_STATUS.TRIALING ? "Trial ends in N days"
                        : advStatus === SUBSCRIPTION_STATUS.ACTIVE ? "Renews in N days"
                        : "Grace ends in N days (≤0 = expired)"}
                    </label>
                    <input type="number" value={advDays} onChange={(e) => setAdvDays(Number(e.target.value))} style={{ width: 90, padding: "8px 10px", borderRadius: 8, border: "1px solid var(--ui-border)" }} />
                  </div>
                )}
                <button className="btn btn--primary" disabled={busy} onClick={() => applySim({ status: advStatus, days: advDays })}>
                  {busy ? "Applying…" : "Apply"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
      <ConfirmModal
        isOpen={showCancelConfirm}
        onClose={closeCancelModal}
        onConfirm={handleCancel}
        variant="warning"
        loading={action === "cancel"}
        title="Cancel subscription?"
        message={`You'll keep full access until the end of your current billing period${
          currentPeriodEnd ? ` (${fmtDate(currentPeriodEnd)})` : ""
        } — there's no refund for the remainder. After that the workspace becomes read-only; your data stays safe and you can resubscribe anytime. Resume before then to undo.`}
        confirmText="Cancel subscription"
        cancelText="Keep subscription"
      >
        <label style={{ display: "block", fontSize: "0.8rem", color: "var(--ui-text-secondary)", fontWeight: 600, marginBottom: 6 }}>
          Mind sharing why? (optional)
        </label>
        <select
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          disabled={action === "cancel"}
          style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid var(--ui-border)", marginBottom: 12, fontSize: "0.875rem" }}
        >
          <option value="">Select a reason…</option>
          {CANCEL_REASONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
        <textarea
          value={cancelComment}
          onChange={(e) => setCancelComment(e.target.value)}
          disabled={action === "cancel"}
          maxLength={500}
          rows={3}
          placeholder="Anything else? (optional)"
          style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid var(--ui-border)", resize: "vertical", fontFamily: "inherit", fontSize: "0.875rem", boxSizing: "border-box" }}
        />
      </ConfirmModal>
    </div>
  );
};

export default SubscriptionsPage;
